import React, { useEffect, useState, useMemo, useCallback } from "react";
import AppLoading from "../components/AppLoading";
import { PagesCommonProps } from "../helpers/pagesUtil";
import dashboard1APIsService from "../hooks/dashboard1APIs";
import TableGraph from "../components/Charts/comps/TableGraph";
import SummaryCardContainer from "../components/SummaryCardsContainer";
import { mergeChartConfigWithApiData } from "../helpers/chartConfigHelper";
import { dashboardGraphsOptions, GOOGLE_CHARTS_TYPES } from "../consts/graphs";
import ChartContainer, { ChartContainerProps } from "../components/Charts/ChartContainer";
import useFetchDashboard1Data from "../hooks/fetchDataComponents/useFetchDashboard1Data";
import { showUserNamesOnRowSelect, showMonthlyUserActivityOnRowSelect, showOrganisationCourseDetails, selectedDataRowForTableDefaultState } from "../helpers/page1";

const { BARCHART, COLUMNCHART, TABLE } = GOOGLE_CHARTS_TYPES;

const COMPLIANCE_TABLE_CHART_IDS = new Set([
  "fetchMostLeastCompliantOrganisationsData",
  "fetchMostLeastCompliantSitesData",
  "fetchMostLeastCompliantRolesData",
]);

const Page1DashboardGraphs: React.FC<PagesCommonProps> = ({ filters, graphs = [] }) => {
  const { data, isLoadingMap, endpointsMap, onScroll, paginationActions, onSortChange, exportActions } = useFetchDashboard1Data({ filters, graphs });
  const [hoveredRowStye, setHoveredRowStye] = useState<{top?: number, left?: number, width?: string} | null>(null);
  const [selectedDataRowForTable, setSelectedDataRowForTable] = useState(selectedDataRowForTableDefaultState);
  const [isDrillDownLoading, setIsDrillDownLoading] = useState(false);

  const filterBlankData = useCallback((chartId: string, chartData: any) => {
    if (!COMPLIANCE_TABLE_CHART_IDS.has(chartId) || !Array.isArray(chartData) || chartData.length <= 1) {
      return chartData;
    }
    const headers = chartData[0];
    const rows = chartData.slice(1);
    const filteredRows = rows.filter((row: any[]) => {
      if (!Array.isArray(row) || row.length === 0) return false;
      const entityName = row[0];
      if (entityName === null || entityName === undefined) return false;
      const trimmed = String(entityName).trim();
      return trimmed !== "" && trimmed.toLowerCase() !== "null" && trimmed.toLowerCase() !== "undefined";
    });
    return [headers, ...filteredRows];
  }, []);

  const wrappedExportActions = useMemo(() => {
    if (!exportActions) return undefined;
    return {
      ...exportActions,
      fetchAllDataForExport: async (chartId: string, sortBy?: string, sortOrder?: 'asc' | 'desc', signal?: AbortSignal) => {
        const result = await exportActions.fetchAllDataForExport(chartId, sortBy, sortOrder, signal);
        if (!result) return result;
        return filterBlankData(chartId, result);
      }
    };
  }, [exportActions, filterBlankData]);

  useEffect(() => {setHoveredRowStye(null)}, [filters]);
  
  if (isLoadingMap.fetchSummaryCardsData) return <AppLoading />;

  const flowType = filters?.invertData?.includes("Most") ? "Most" : "Least";



  const baseChartsConfig: ChartContainerProps[] = [
    {
      boxWidth: "45%",
      chartType: COLUMNCHART,
      title: "Organisations by Compliance Group",
      chartId: "fetchOrganisationsByComplianceGroupData",
      options: dashboardGraphsOptions.BarChartChartOptions.GetByColumnLength(
        data?.fetchOrganisationsByComplianceGroupData?.data?.[0],
        { h: "% Group", v: "No. of Organisations" },
      ),
      onRowSelect: (param: any) => showUserNamesOnRowSelect(
        "fetchOrganisationsByComplianceGroupData", param,
        (data?.fetchOrganisationsByComplianceGroupData as any),
        setHoveredRowStye, setSelectedDataRowForTable,
      ),
    },
    {
      boxWidth: "45%",
      chartType: COLUMNCHART,
      title: "Users by Compliance Group",
      chartId: "fetchUsersByComplianceGroupData",
      options: dashboardGraphsOptions.BarChartChartOptions.GetByColumnLength(
        data?.fetchUsersByComplianceGroupData?.data?.[0],
        { h: "% Group", v: "No. of Users" },
      ),
      onRowSelect: (param: any) => showUserNamesOnRowSelect(
        "fetchUsersByComplianceGroupData", param,
        (data?.fetchUsersByComplianceGroupData as any),
        setHoveredRowStye, setSelectedDataRowForTable,
      ),
    },
    {
      onScroll,
      boxWidth: "45%",
      chartType: COLUMNCHART,
      title: 'New Users per Month',
      chartId: "fetchNewUsersPerMonthData",
      options: dashboardGraphsOptions.BarChartOptions.GetByColumnLength(
        data?.fetchNewUsersPerMonthData?.data?.[0],
      ),
      onRowSelect: (param: any) => showUserNamesOnRowSelect(
        "fetchNewUsersPerMonthData", param,
        (data?.fetchNewUsersPerMonthData as any),
        setHoveredRowStye, setSelectedDataRowForTable,
      ),
    },
    {
      //onScroll,
      boxWidth: "45%",
      chartType: BARCHART,
      chartId: "fetchMostLeastCompliantCoursesData",
      title: `${flowType} Compliant Courses`,
      paginationActions,
      options: (() => {
        const baseOptions =
          dashboardGraphsOptions.BarChartOptions.GetByColumnLength(
            data?.fetchMostLeastCompliantCoursesData?.data?.[0]
          );
        return {
          ...baseOptions,
          chartArea: {
            ...baseOptions.chartArea,
            left: 100,
            height: '80%',
          },
        };
      })(),
    },
    {
      boxWidth: "45%",
      chartType: COLUMNCHART,
      title: "Monthly User Activity",
      chartId: "fetchMonthlyUserActivityData",
      options: (() => {
        const base = dashboardGraphsOptions.BarChartOptions.GetByColumnLength(
          data?.fetchMonthlyUserActivityData?.data?.[0],
          { h: "Month", v: "No. of Users" },
        );
        return {
          ...base,
          isStacked: false,
          colors: ["#08305D", "#6A98DD", "#8A8C8E"],
          vAxis: { ...base.vAxis, scaleType: "log", minValue: 1 },
        };
      })(),
      onRowSelect: (param: any) => showMonthlyUserActivityOnRowSelect(
        "fetchMonthlyUserActivityData",
        param,
        (data?.fetchMonthlyUserActivityData as any),
        setHoveredRowStye,
        setSelectedDataRowForTable,
      ),
    },
    {
      onScroll,
      onSortChange,
      chartType: TABLE,
      title: `${flowType} Compliant Organisations`,
      options: dashboardGraphsOptions.TableChartOptions,
      chartId: "fetchMostLeastCompliantOrganisationsData",
    },
    {
      onScroll,
      onSortChange,
      chartType: TABLE,
      title: `${flowType} Compliant Sites`,
      chartId: "fetchMostLeastCompliantSitesData",
      options: dashboardGraphsOptions.TableChartOptions,
    },
    {
      onScroll,
      onSortChange,
      chartType: TABLE,
      title: `${flowType} Compliant Roles`,
      chartId: "fetchMostLeastCompliantRolesData",
      options: dashboardGraphsOptions.TableChartOptions,
    },
    {
      onScroll,
      onSortChange,
      boxWidth: "95%",
      chartType: TABLE,
      hideColumnsByIndex: ["0"],
      title: "Organisation Course Summary",
      chartId: "fetchOrganisationCourseSummaryData",
      options: dashboardGraphsOptions.TableChartOptions,
      onRowSelect: (param: any) => showOrganisationCourseDetails(
        "fetchOrganisationCourseSummaryData", param,
        (data?.fetchOrganisationCourseSummaryData as any),
        setHoveredRowStye, setSelectedDataRowForTable, setIsDrillDownLoading,
        (organisationId: number) => dashboard1APIsService.fetchOrganisationCourseSummaryDrillDown(organisationId, filters)
      ),
    },
    {
      onScroll,
      onSortChange,
      boxWidth: "95%",
      chartType: TABLE,
      title: "MI Report",
      chartId: "fetchMiReportData",
      options: dashboardGraphsOptions.TableChartOptions,
    },
  ];

  // Merge base configuration with API data and filter/sort
  const ChartsListData: ChartContainerProps[] = mergeChartConfigWithApiData(baseChartsConfig, graphs);

  return (
    <>
      <SummaryCardContainer summaryCardsData={data?.fetchSummaryCardsData?.data as any} />
      <div className="charts-container">
        {ChartsListData.map((props, idx) => {
          const chartId = props.chartId;
          if (!chartId) return null;
          const rawChartData = data?.[chartId as keyof typeof data]?.data;
          const filteredChartData = filterBlankData(chartId, rawChartData);
          const rawTotal = data?.[chartId as keyof typeof data]?.total;
          const filteredTotal = (rawChartData && filteredChartData && Array.isArray(rawChartData) && Array.isArray(filteredChartData))
            ? (rawTotal !== undefined ? Math.max(0, rawTotal - (rawChartData.length - filteredChartData.length)) : undefined)
            : rawTotal;

          return (
            <ChartContainer 
              {...props} 
              key={idx} 
              index={idx}
              queryParams={filters}
              exportActions={wrappedExportActions}
              data={filteredChartData}
              total={filteredTotal}
              endpoint={endpointsMap?.[chartId as keyof typeof endpointsMap]}
              isLoading={isLoadingMap?.[chartId as keyof typeof isLoadingMap]}
            />
          );
        })}
      </div>
      {hoveredRowStye !== null && (
        <div className='chart-hovered' style={hoveredRowStye}>
          <div className='chart-hovered-buttons'>
            <button
              className="close-button"
              about="tools"
              onClick={() => {
                setHoveredRowStye(null);
                setIsDrillDownLoading(false);
              }}
              style={{
                backgroundSize: '13px',
                backgroundImage: `url('${process.env.REACT_APP_PUBLIC_URL || process.env.PUBLIC_URL || ''}/close.png')`,
              }}
            />
          </div>
          {isDrillDownLoading ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '200px',
              flexDirection: 'column',
              color: 'black'
            }}>
              <div style={{ marginBottom: '10px' }}>Loading course details...</div>
              <div className="spinner" style={{
                width: '20px',
                height: '20px',
                border: '2px solid #f3f3f3',
                borderTop: '2px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
            </div>
          ) : (
            <div style={{ width: hoveredRowStye.width || "200px", height: "200px", color: "black" }}>
              <TableGraph data={selectedDataRowForTable} isFullHeight />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Page1DashboardGraphs;
